import { useEffect, useRef, useState } from "react";
import { Button, message } from "antd";
import { CloseOutlined, CloudUploadOutlined, LoadingOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { memberState } from "../../../state/atoms";
import { env } from "../../../env/env";
import AssetApi from "../../../api/AssetApi";
import YextApi, { yextErrorMessage } from "../../../api/YextApi";
import "./ProfileVisualAssetsSection.scss";

type AssetSectionKey = "logo" | "cover" | "interior" | "exterior" | "foodDrink";

const SECTIONS: { key: AssetSectionKey; label: string; multi: boolean }[] = [
  { key: "logo", label: "Logo", multi: false },
  { key: "cover", label: "Cover Image", multi: false },
  { key: "interior", label: "Interior Photos", multi: true },
  { key: "exterior", label: "Exterior Photos", multi: true },
  { key: "foodDrink", label: "Food and Drink Photos", multi: true },
];

interface UploadedFile {
  id: string;
  name: string;
  url: string;
}

type AssetState = Record<AssetSectionKey, UploadedFile[]>;

const EMPTY: AssetState = {
  logo: [],
  cover: [],
  interior: [],
  exterior: [],
  foodDrink: [],
};

// Logo goes to the LOGOS asset category; every other section is a photo.
function categoryFor(key: AssetSectionKey): string {
  return key === "logo" ? "LOGOS" : "PHOTOS";
}

function fileFromUrl(url: string): UploadedFile {
  return { id: url, name: url.split("/").pop() ?? url, url };
}

interface Props {
  locationId?: number;
  onDirtyChange?: (dirty: boolean) => void;
}

export function ProfileVisualAssetsSection({ locationId, onDirtyChange }: Props) {
  const member = useRecoilValue(memberState);
  const teamId = member?.teamId || "";

  const [assets, setAssets] = useState<AssetState>(EMPTY);
  const [savedSnapshot, setSavedSnapshot] = useState<AssetState>(EMPTY);
  const [uploading, setUploading] = useState(0);
  const [saving, setSaving] = useState(false);

  const dirty = JSON.stringify(assets) !== JSON.stringify(savedSnapshot);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // Load the persisted asset URLs so they survive a refresh.
  useEffect(() => {
    if (!locationId) return;
    YextApi.getProfile(locationId).then(({ data }) => {
      const v = data.visualAssets;
      if (!v) return;
      const loaded: AssetState = {
        logo: v.logo ? [fileFromUrl(v.logo)] : [],
        cover: v.cover ? [fileFromUrl(v.cover)] : [],
        interior: (v.interior ?? []).map(fileFromUrl),
        exterior: (v.exterior ?? []).map(fileFromUrl),
        foodDrink: (v.foodDrink ?? []).map(fileFromUrl),
      };
      setAssets(loaded);
      setSavedSnapshot(loaded);
    });
  }, [locationId]);

  async function addFiles(key: AssetSectionKey, files: FileList | null, multi: boolean) {
    if (!files || files.length === 0) return;
    if (!teamId) {
      message.error("No team found for upload.");
      return;
    }
    const accepted = multi ? Array.from(files) : [files[0]];
    setUploading((n) => n + 1);
    try {
      const uploaded = await Promise.all(
        accepted.map(async (f) => {
          const { data } = await AssetApi.upload(teamId, categoryFor(key), f);
          return { id: data.id ?? data.path ?? f.name, name: data.name ?? f.name, url: `${env.s3Path}/${data.path}` };
        })
      );
      setAssets((s) => ({ ...s, [key]: multi ? [...s[key], ...uploaded] : uploaded }));
    } catch (e) {
      message.error("Failed to upload image.");
    } finally {
      setUploading((n) => n - 1);
    }
  }

  function removeFile(key: AssetSectionKey, id: string) {
    setAssets((s) => ({ ...s, [key]: s[key].filter((f) => f.id !== id) }));
  }

  function discardChanges() {
    setAssets(savedSnapshot);
  }

  async function saveChanges() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setSaving(true);
    try {
      await YextApi.syncLocation(locationId, {
        visualAssets: {
          logo: assets.logo[0]?.url,
          cover: assets.cover[0]?.url,
          interior: assets.interior.map((f) => f.url),
          exterior: assets.exterior.map((f) => f.url),
          foodDrink: assets.foodDrink.map((f) => f.url),
        },
      });
      setSavedSnapshot(assets);
      message.success("Visual assets saved.");
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to save visual assets."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="profile-visual-assets">
      <h3 className="pva-title">Visual Assets</h3>

      {SECTIONS.map((s) => (
        <section key={s.key} className="pva-group">
          <span className="pva-group-label">{s.label}</span>
          {assets[s.key].length > 0 && (
            <div className={`pva-thumbs${s.multi ? " multi" : " single"}`}>
              {assets[s.key].map((f) => (
                <div className="pva-thumb" key={f.id}>
                  <img src={f.url} alt={f.name} />
                  <button
                    type="button"
                    className="pva-thumb-remove"
                    aria-label="Remove image"
                    onClick={() => removeFile(s.key, f.id)}
                  >
                    <CloseOutlined />
                  </button>
                </div>
              ))}
            </div>
          )}
          <AssetUploader
            multi={s.multi}
            onFiles={(files) => addFiles(s.key, files, s.multi)}
          />
        </section>
      ))}

      <footer className="pva-actions">
        <Button className="pva-discard-btn" size="large" onClick={discardChanges} disabled={saving}>
          Discard Changes
        </Button>
        <Button
          type="primary"
          danger
          className="pva-save-btn"
          size="large"
          onClick={saveChanges}
          loading={saving}
          disabled={uploading > 0}
        >
          {uploading > 0 ? "Uploading…" : "Save Changes"}
        </Button>
      </footer>
    </div>
  );
}

interface AssetUploaderProps {
  multi: boolean;
  onFiles: (files: FileList | null) => void | Promise<void>;
}

function AssetUploader({ multi, onFiles }: AssetUploaderProps) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function emit(files: FileList | null) {
    setBusy(true);
    try {
      await onFiles(files);
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    emit(e.dataTransfer.files);
  }

  return (
    <div
      className={`pva-dropzone${dragging ? " dragging" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {busy ? <LoadingOutlined className="pva-cloud-icon" /> : <CloudUploadOutlined className="pva-cloud-icon" />}
      <p className="pva-dropzone-text">Drag and drop images here or</p>
      <Button
        className="pva-choose-btn"
        loading={busy}
        onClick={() => inputRef.current?.click()}
      >
        Choose Media
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multi}
        style={{ display: "none" }}
        onChange={(e) => {
          emit(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
